import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { baseGrid } from '@helpers/grid';
import { Device, from } from '@helpers/media';
import srOnly from '@helpers/srOnly';
import { contentStyles, h3, subtitle } from '@helpers/typography';
import ButtonStyles from '@stories/Components/Buttons/Button/Button.styles';
import SectionWrapper, { ContentType, } from '@stories/Components/Misc/SectionWrapper/SectionWrapper';
import styled from 'styled-components';
const Container = styled(SectionWrapper) `
  --sectionSpacing: ${fluid(32, 72)};
`;
const Inner = styled.div `
  ${baseGrid};

  padding: 0;
  overflow: hidden;
  box-shadow: 0px 2px 7px 0px rgba(0, 0, 0, 0.25);
  border-radius: ${fluid(30, 60)} ${fluid(30, 60)} ${fluid(30, 60)} 0;
`;
const Controls = styled.div `
  --bgColour: ${brand.blue.gradient};
  --fgColour: ${brand.global.white};

  grid-column: 1 / span 12;
  background: var(--bgColour);
  color: var(--fgColour);
  padding: ${fluid(32, 83)} ${fluid(24, 37)} ${fluid(16, 83)};

  [data-theme='green'] & {
    --bgColour: ${brand.green.gradient};
    --direction: 0deg;
  }

  [data-theme='light-green'] & {
    --bgColour: ${brand.green.light};
    --fgColour: ${brand.blue.navy};
  }

  [data-theme='light-blue'] & {
    --bgColour: ${brand.blue.light};
    --fgColour: ${brand.blue.navy};
  }

  @media ${from(Device.TabletLarge)} {
    grid-column: 1 / span 4;
  }
`;
const SubTitle = styled.p `
  ${subtitle};

  color: var(--fgColour);
`;
const Heading = styled.h2 `
  ${h3};

  margin-bottom: ${fluid(16, 32)};
`;
const TabsLabel = styled.p `
  ${fonts.proximaNova.regular};

  font-size: 14px;
  line-height: 24px;
  margin: 0 0 ${fluid(8, 32)};

  &[data-hidden='true'] {
    ${srOnly};
  }
`;
const Tabs = styled.div `
  --direction: 0deg;

  --tabActiveBgColour: ${brand.blue.light};
  --tabInActiveBgColour: transparent;
  --tabActiveFgColour: ${brand.blue.navy};
  --tabIconBorderColour: ${brand.global.beige};
  --tabIconBorderActiveColour: ${brand.blue.default};
  --tabIconActiveBgColour: ${brand.blue.gradient};
  --tabIconInActiveBgColour: ${brand.global.beige};
  --tabIconActiveFgColour: ${brand.global.white};
  --tabIconInActiveFgColour: ${brand.green.default};

  [data-theme='green'] & {
    --direction: 180deg;
    --tabActiveBgColour: ${brand.green.light};
    --tabInActiveBgColour: transparent;
  }

  [data-theme='light-blue'] & {
    --tabActiveBgColour: ${brand.blue.dark};
    --tabInActiveBgColour: transparent;
    --tabActiveFgColour: ${brand.global.white};
  }

  [data-theme='light-green'] & {
    --tabActiveBgColour: ${brand.blue.dark};
    --tabInActiveBgColour: transparent;
    --tabActiveFgColour: ${brand.global.white};
  }

  display: flex;
  gap: ${fluid(10, 12)};
  width: calc(100% + 24px);
  padding-right: 24px;
  overflow: auto;

  ${ButtonStyles.Container} {
    --buttonHeight: ${fluid(44, 74)};
    --textColour: var(--tabActiveBgColour);

    ${ButtonStyles.IconWrapper} {
      --direction: 180deg;
      --tabIconInActiveFgColour: ${brand.global.white};
      --tabIconActiveBgColour: ${brand.green.gradient};
      --tabIconBorderActiveColour: ${brand.green.default};
      --tabIconInActiveFgColour: ${brand.green.default};
    }

    &[data-parent-theme='light-blue'],
    &[data-parent-theme='light-green'] {
      ${ButtonStyles.IconWrapper} {
        --direction: 180deg;
        --tabIconBorderColour: ${brand.green.default};
        --tabIconInActiveFgColour: ${brand.global.white};
        --tabIconInActiveBgColour: ${brand.green.gradient};
        --tabIconActiveBgColour: ${brand.green.gradient};
        --tabIconBorderActiveColour: ${brand.green.default};
      }
    }

    &[data-icon-theme=${ContentType.Default}] {
      ${ButtonStyles.IconWrapper} {
        --direction: 180deg;
        --tabIconActiveBgColour: ${brand.green.gradient};
        --tabIconBorderActiveColour: ${brand.green.default};
        --tabIconInActiveFgColour: ${brand.green.default};
      }
    }

    &[data-icon-theme=${ContentType.Savings}] {
      ${ButtonStyles.IconWrapper} {
        --direction: 180deg;
        --tabIconActiveBgColour: ${brand.blue.gradient};
        --tabIconBorderActiveColour: ${brand.blue.default};
        --tabIconInActiveFgColour: ${brand.blue.default};
      }
    }

    &[data-icon-theme=${ContentType.Intermediaries}] {
      ${ButtonStyles.IconWrapper} {
        --tabIconActiveFgColour: ${brand.green.light};
        --tabIconInActiveFgColour: ${brand.blue.navy};
        --tabIconBorderActiveColour: ${brand.blue.navy};
        --tabIconActiveBgColour: ${brand.blue.navy};
      }
    }

    &[data-icon-theme=${ContentType.Default}][data-parent-theme='light-blue'],
    &[data-icon-theme=${ContentType.Default}][data-parent-theme='light-green'] {
      ${ButtonStyles.IconWrapper} {
        --tabIconInActiveFgColour: ${brand.global.white};
        --direction: 180deg;
        --tabIconBorderColour: ${brand.green.default};
        --tabIconInActiveBgColour: ${brand.green.gradient};
        --tabIconActiveBgColour: ${brand.green.gradient};
        --tabIconBorderActiveColour: ${brand.green.default};
      }
    }

    &[data-icon-theme=${ContentType.Savings}][data-parent-theme='light-blue'],
    &[data-icon-theme=${ContentType.Savings}][data-parent-theme='light-green'] {
      ${ButtonStyles.IconWrapper} {
        --tabIconInActiveFgColour: ${brand.global.white};
        --tabIconBorderColour: ${brand.blue.default};
        --tabIconInActiveBgColour: ${brand.blue.gradient};
        --tabIconActiveBgColour: ${brand.blue.gradient};
      }
    }

    &[data-icon-theme=${ContentType.Intermediaries}][data-parent-theme='light-blue'],
    &[data-icon-theme=${ContentType.Intermediaries}][data-parent-theme='light-green'] {
      ${ButtonStyles.IconWrapper} {
        --tabIconActiveFgColour: ${brand.blue.navy};
        --tabIconInActiveFgColour: ${brand.green.light};
        --tabIconBorderColour: ${brand.blue.navy};
        --tabIconInActiveBgColour: ${brand.blue.navy};
        --tabIconBorderActiveColour: ${brand.green.light};
        --tabIconActiveBgColour: ${brand.green.light};
      }
    }

    white-space: nowrap;
    background: var(--tabInActiveBgColour);
    border-radius: 10px 10px 10px 0;
    border-color: var(--tabActiveBgColour);

    &:hover {
      background: var(--tabActiveBgColour);
      color: var(--tabActiveFgColour);

      ${ButtonStyles.IconWrapper} {
        background: var(--tabIconInActiveBgColour);
      }
    }
  }

  ${ButtonStyles.Container}[aria-selected='true'] {
    color: var(--tabActiveFgColour);
    background: var(--tabActiveBgColour);
    border-color: var(--tabActiveBgColour);

    ${ButtonStyles.IconWrapper} {
      --iconTextColour: var(--tabIconActiveFgColour);
      background: var(--tabIconActiveBgColour);
      border-color: var(--tabIconBorderActiveColour);
    }
  }

  ${ButtonStyles.Text} {
    ${fonts.proximaNova.semiBold};
    font-size: ${fluid(16, 20)};
  }

  ${ButtonStyles.IconWrapper} {
    --iconHeight: ${fluid(17, 22)};
    --padding: ${fluid(6, 9)};
    --iconTextColour: var(--tabIconInActiveFgColour);

    background: var(--tabIconInActiveBgColour);
    border: 2px solid var(--tabIconBorderColour);
  }

  &[data-hidden='true'] {
    ${srOnly};
  }

  @media ${from(Device.TabletLarge)} {
    flex-direction: column;
    padding-right: 0;
    overflow: visible;
  }

  @media ${from(Device.DesktopLarge)} {
    width: 100%;
  }
`;
const FaqGroups = styled.div `
  grid-column: 1 / span 12;

  display: grid;
  grid-template-columns: 1fr;
  padding: ${fluid(16, 87)} ${fluid(16, 89)} ${fluid(16, 69)} ${fluid(16, 68)};

  @media ${from(Device.TabletLarge)} {
    grid-column: 5 / span 8;
  }
`;
const AccordionWrapper = styled.div `
  ${transition('opacity, transform, visibility', 0.5)};

  --titleColour: ${brand.blue.dark};

  [data-theme='green'] &,
  [data-theme='light-green'] & {
    --titleColour: ${brand.green.dark};
  }

  grid-column: 1;
  grid-row: 1;
  position: relative;

  &::before {
    left: 0;
  }

  &::after {
    right: 0;
    transform: scaleX(-1);
  }

  ${Container}[data-animate='false'] &,
  &[data-active='false'] {
    opacity: 0;
    pointer-events: none;
    visibility: hidden;
    height: 0;
    transition-delay: 50ms;
  }

  ${Container}[data-animate='false'] & {
    transform: translateY(20px);
  }

  ${Container}[data-delays='true'] & {
    transition-delay: 750ms;
  }
`;
const FaqContent = styled.div `
  padding: 12px 0 8px;

  @media ${from(Device.TabletLarge)} {
    padding: ${fluid(4, 4)} 0 0;
  }
`;
const Answer = styled.div `
  ${contentStyles};
  color: ${brand.blue.navy};

  // Changed to dark blue for accessibility (contrast failed for default blue)
  --listColour: ${brand.blue.dark};

  [data-theme='green'] &,
  [data-theme='light-green'] & {
    --listColour: ${brand.green.default};
  }
`;
const ExtraFaqs = styled.div `
  height: 0;
  visibility: hidden;

  &[data-show-extra='true'] {
    height: auto;
    visibility: visible;
  }
`;
const ButtonWrapper = styled.div `
  display: flex;
  gap: ${fluid(12, 38)};
  flex-wrap: wrap;
  margin-top: ${fluid(12, 12)};
`;
const FaqsStyles = {
    AccordionWrapper,
    Answer,
    ButtonWrapper,
    Container,
    Controls,
    ExtraFaqs,
    FaqContent,
    FaqGroups,
    Heading,
    Inner,
    SubTitle,
    Tabs,
    TabsLabel,
};
export default FaqsStyles;
